import styled, { css, CSSObject, keyframes } from "styled-components";
// deps
import { Text } from "@swiggy-private/dls-web-core/dist/components/Text";
// utils
import {
    STATE_CHANGE_DELAY,
    XP2_MASTHEAD,
    LINES_BACKGROUND,
} from "@super-ui/components/m2c-swiggy-black-halfcard/constants";
import { getBackgroundImage } from "@super-ui/components/m2c-swiggy-black-halfcard/helpers";

const TITLE_BACKGROUND_GRADIENT = "linear-gradient(90deg, #836662 -12.93%, #DDD1C9 96.4%)";

const getVariantSpecificStyles = (isVariantV1: boolean) => {
    if (isVariantV1) {
        return {
            backgroundImage: getBackgroundImage(LINES_BACKGROUND, { width: 800 }),
            backgroundSize: "contain",
            backgroundRepeat: "no-repeat",
        };
    }
    return {
        paddingTop: 0,
    };
};

export const BullsEyeWrapper = styled.div<{ isVariantV1: boolean }>(({ isVariantV1, theme }) => {
    return {
        padding: `${theme.spacing.large}`,
        borderRadius: `${theme.cornerRadius.xLarge} ${theme.cornerRadius.xLarge} 0px 0px`,
        boxShadow: `0px calc(${theme.spacing.xxSmall} * -1) ${theme.spacing.medium} rgba(0, 0, 0, 0.1)`,
        transition: `${STATE_CHANGE_DELAY}ms`,
        paddingBottom: `calc(var(--sab, 0px) + ${theme.spacing.large})`,
        ...getVariantSpecificStyles(isVariantV1),
    };
});

export const BullsEyeHalfCard = styled.div(({ theme }) => {
    return {
        backgroundColor: theme.colors[theme.colorMode].background_Primary,
        borderRadius: `${theme.cornerRadius.xLarge} ${theme.cornerRadius.xLarge} 0px 0px`,
        background: "linear-gradient(38deg, #10030C 20.37%, #460C39 115.64%)",
    };
});

export const HalfCard = styled.div(({ theme }) => ({
    paddingTop: theme.spacing.medium,
}));

export const SwiggyLogo = styled.div<{ isBlackRenewalType: boolean }>(({ theme, isBlackRenewalType = false }) => ({
    width: "152px",
    height: "26px",
    margin: "0 auto",
    marginBottom: theme.spacing[isBlackRenewalType ? "xSmall" : "small"],
}));

export const MastHeadLogo = styled.div(({ theme }) => ({
    width: "100vw",
    height: "46.4vw",
    backgroundImage: getBackgroundImage(XP2_MASTHEAD, { width: 500 }),
    backgroundSize: "cover",
    paddingTop: theme.spacing.xxLarge,
    marginBottom: theme.spacing.xxSmall,
    marginLeft: "50%",
    transform: "translateX(-50%)",
}));

export const HalfCardContainer = styled.div(({ theme }) => ({
    width: "100%",
    marginTop: theme.spacing.small,
}));

export const TitleText = styled(Text)(() => ({
    textAlign: "center" as CSSObject["textAlign"],
    background: TITLE_BACKGROUND_GRADIENT,
    backgroundClip: "text",
    color: "transparent",
}));

export const LottiContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing.xxLarge,
}));

export const DefaultHeaderContainer = styled.div(({ theme }) => ({
    background: theme.colors[theme.colorMode].text_disabled,
    borderTopLeftRadius: theme.cornerRadius.medium,
    borderTopRightRadius: theme.cornerRadius.medium,
    height: "40px",
    alignContent: "center",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",

    img: {
        position: "absolute",
        marginLeft: "4px",
    },
}));

export const HalfCardDetails = styled.div(({ theme }) => ({
    margin: "auto",
    textAlign: "center",
    paddingTop: theme.spacing.xSmall,
    borderRadius: theme.cornerRadius.medium,
    border: "1px solid",
    borderImageSource:
        "linear-gradient(180deg,rgba(255,255,255,0) 25%,rgba(255,255,255,0.2) 67.31%,rgba(255,255,255,0) 90%)",
    borderImageSlice: 1,
}));

export const CtaContainer = styled.div(({ theme }) => ({
    marginTop: theme.spacing.large,
    marginBottom: theme.spacing.xSmall,
}));

export const ViewAllBenefitsButton = styled.div(({ theme }) => ({
    marginTop: theme.spacing.large,
    display: "flex",
    justifyContent: "center",
}));

const generateFlipAnimation = (index: number) => {
    const [start, end] = index % 2 === 0 ? ["0deg", "180deg"] : ["180deg", "0deg"];
    return keyframes`
      0% { transform: rotateX(${start}); }
      25% { transform: rotateX(${end}); }
      50% { transform: rotateX(${start}); }
      75%, 100% { transform: rotateX(${end}); }
    `;
};

export const AnimatedText = styled(Text)<{ index: number }>`
    position: absolute;
    text-transform: uppercase;
    backface-visibility: hidden;
    transform-style: preserve-3d;
    transform: ${({ index }) => (index % 2 === 0 ? "rotateX(0deg)" : "rotateX(180deg)")};
    ${({ index }) => css`
        animation: ${generateFlipAnimation(index)} 6s forwards;
        animation-delay: ${1.5}s;
    `}
`;

export const ContentListContainer = styled.div<{ isBlackRenewalType: boolean }>(({ theme, isBlackRenewalType }) => ({
    display: "inline-grid",
    justifyItems: isBlackRenewalType ? "center" : "flex-start",
    gridGap: theme.spacing.xSmall,
    margin: `${isBlackRenewalType ? theme.spacing.small : theme.spacing.large} 0`,
    width: isBlackRenewalType ? "100%" : "auto",
    marginRight: theme.spacing.xxSmall,
}));

export const ContentContainer = styled.div(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing.xSmall,
}));

// variant v2
export const ContainerV2 = styled.div(({ theme }) => ({
    display: "flex",
    minHeight: "200px",
    height: "fit-content",
    paddingTop: theme.spacing.xLarge,
}));

export const ContentTextWrapper = styled.div(() => ({
    display: "flex",
    flexDirection: "column" as CSSObject["flexDirection"],
    alignItems: "center",
}));

export const ContentText = styled(Text)<{ isBlackRenewalType: boolean }>((isBlackRenewalType) => ({
    "@media (min-width: 400px)": isBlackRenewalType
        ? {}
        : {
              fontSize: "16px",
          },
}));
